import React, { useState, useEffect } from "react";
import { Dropdown, Table } from "react-bootstrap";
import { Link, NavLink, useParams } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { useTranslate } from "../hooks/LanguageProvider";
import { formatDateInServerUtc, formatNotificationMessage, parseServerUtcDate, toTimestamp } from "../utilities/functions";
import { PropagateLoader } from "react-spinners";

interface NotificationsProps {
  id_notification: number;
  id_groupe_alarme: number;
  name_groupe_alarme: string;
  name_alarme: string;
  id_user: number;
  type: number | string;
  webhook_state?: 'pending' | 'retry' | 'review' | 'delivered';
  webhook_attempts?: number;
  webhook_http_status?: number;
  webhook_error?: string;
  webhook_delivered_at?: string;
  webhook_next_attempt_at?: string;
  trip_created_at?: string | null;
  trip_event_status?: 'arrived' | 'delayed' | null;
  message: string;
  severity: number;
  timestamp: string;
  state: number;
  attached: number;
  date_start: string;
  immatriculation_vehicule: string;
}

export function Notifications() {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const { id_notification } = useParams<{ id_notification?: string }>();

  const { translate } = useTranslate();
  const getPositionTrackingWindow = (timestamp: string) => {
    const date = parseServerUtcDate(timestamp);
    const from = new Date(date.getTime() - 2 * 60 * 1000);
    const to = new Date(date.getTime() + 2 * 60 * 1000);
    return {
      from: formatDateInServerUtc(from),
      to: formatDateInServerUtc(to),
    };
  };
  let [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [list_Notifications, setNotifications] = useState<NotificationsProps[]>(
    []
  );
  const id_user = localStorage.getItem("userid");
  const [showCreateAlarmModal, setShowCreateAlarmModal] = useState(false);
  const handleshowCreateAlarmModal = () => setShowCreateAlarmModal(true);
  const handleCloseCreateAlarmModal = () => setShowCreateAlarmModal(false);
  const [loading, setLoading] = useState(true); // Add loading state
  const [pageCount, setPageCount] = useState(0);
  let [total, setTotal] = useState(0);
  const [column, setSortColum] = useState("0");
  const [sort, setSort] = useState("ASC");
  const [search, setSearch] = useState(id_notification || "");
  const [type, setType] = useState(0);
  const [typeSearch, setTypeSearch] = useState("Notification ID");
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const handleSubmit = () => {
    // Votre logique de soumission ici
  };

  const getNotifications = async (
    limitValue: number,
    currentPage: number,
    search: string,
    type: number,
    column: string,
    sort: string,
    showLoader = true
  ) => {
    try {
      if (showLoader) setLoading(true);

      // Préparation des données à envoyer
      const bodyData = JSON.stringify({
        limitValue,
        currentPage,
        search,
        type,
        id_user,
        column,
        sort,
      });

      // Récupération du nombre total de pages
      const totalPagesResponse = await fetch(
        `${backendUrl}/api/notification/totalpage`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: bodyData,
          mode: "cors",
        }
      );

      const totalPagesJson = await totalPagesResponse.json();
      const total = totalPagesJson[0]["count"];
      setTotal(total);

      const alarmsResponse = await fetch(
        `${backendUrl}/api/notification/search`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: bodyData,
          mode: "cors",
        }
      );

      const data = await alarmsResponse.json();
      setPageCount(Math.ceil(total / limitValue));
      setLimit(limitValue);
      setNotifications(data);
      return data;
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageClick = async (data: any) => {
    let currentPage = data.selected + 1;
    const commentsFormServer = await getNotifications(
      limit,
      currentPage,
      search,
      type,
      column,
      sort
    );
    setNotifications(commentsFormServer);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    getNotifications(limit, currentPage, search, type, column, sort);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      getNotifications(limit, currentPage, search, type, column, sort, false);
    }, 30000);

    return () => clearInterval(interval);
  }, [limit, currentPage, search, type, column, sort]);

  const handleSelectChange = async (event: any) => {
    const newValue = event.target.value;
    setCurrentPage(1); //
    setLimit(newValue);
    const commentsFormServer = await getNotifications(
      parseInt(newValue),
      1,
      search,
      type,
      column,
      sort
    );
    setNotifications(commentsFormServer);
    window.scrollTo(0, 0);
  };

  const [selectedColumns, setSelectedColumns] = useState({
    id_notification: true,
    id_groupe_alarme: true,
    name_groupe_alarme: true,
    name_alarme: true,
    type: true,
    message: true,
    severity: true,
    timestamp: true,
    infos: true,
    attached: true,
    date_start: true,
    date_end: true,
  });

  const handleColumnChange = (column: string) => {
    setSelectedColumns((prevState: any) => ({
      ...prevState,
      [column]: !prevState[column],
    }));
  };

  const handleTypeSearch = (event: any) => {
    const selectedValue = event.target.textContent;

    switch (selectedValue) {
      case translate("ID"):
        setType(0);
        break;
      case translate("Group Alarms"):
        setType(1);
        break;
      case translate("Alarms"):
        setType(2);
        break;
      case translate("Details"):
        setType(3);
        break;
      case translate("Attached to"):
        setType(4);
        break;
      case translate("Start date"):
        setType(5);
        break;
      case translate("End date"):
        setType(6);
        break;
      default:
        // console.log("Unknown selection");
        break;
    }

    setTypeSearch(selectedValue);
    // console.log("Selected value:", selectedValue);
  };

  const handleAdvancedSearch = async (event: any) => {
    const newValue = event.target.value;
    setSearch(newValue);
    await getNotifications(limit, currentPage, newValue, type, column, sort);
  };

  const handleResetSearch = async () => {
    setSearch("");
    await getNotifications(limit, currentPage, "", type, column, sort);
  };

  const handleSortingColum = (currentColumn: string) => {
    setSortColum(currentColumn);
    sort == "ASC" ? setSort("DESC") : setSort("ASC");
    setCurrentPage(1);
    getNotifications(limit, 1, search, type, column, sort);
  };

  return (
    <>
      <div className="row">
        <div className="col-md-6 col-sm-12">
          <h4>
            <span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                className="feather feather-bell"
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
              </svg>
            </span>
            {translate("Notifications")} ({total})
          </h4>
        </div>
        <div className="col-md-6 col-sm-12 text-right">
          <NavLink to="/alarms" className="btn btn-outline-info  mr-1">
            <i className="las la-cog mr-3"></i>
            {translate("Configuration Alarme")}
          </NavLink>
        </div>
      </div>
      <div className="row">
        <div
          className="col-md-4"
          style={{ margin: "0px 0px 10px 0px", padding: "10px" }}
        >
          <div className="input-group">
            <Dropdown>
              <Dropdown.Toggle variant="link" id="dropdown-basic">
                <i
                  className="fas fa-chevron-down"
                  style={{ fontSize: "20" }}
                ></i>
              </Dropdown.Toggle>
              <Dropdown.Menu onClick={handleTypeSearch}>
                <Dropdown.Item>{translate("Notification ID")}</Dropdown.Item>
                <Dropdown.Item>{translate("Group")}</Dropdown.Item>
                <Dropdown.Item>{translate("Alert")}</Dropdown.Item>
                <Dropdown.Item>{translate("Details")}</Dropdown.Item>
                <Dropdown.Item>{translate("Attached to")}</Dropdown.Item>
                <Dropdown.Item>{translate("Start date")}</Dropdown.Item>
                <Dropdown.Item>{translate("End date")}</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <input
              type="text"
              placeholder={` By ${typeSearch}`}
              value={search}
              onChange={handleAdvancedSearch}
              className="form-control"
            />
            <button
              type="button"
              className="btn btn-reset"
              onClick={handleResetSearch}
            >
              <i className="las la-times" style={{ color: "#fff" }}></i>
            </button>
          </div>
        </div>
        <div className="col-md-8 d-flex justify-content-end align-items-center">
          <div className="dataTables_length" id="DataTables_Table_0_length">
            <label style={{ marginBottom: "0" }}>
              {translate("Show")}
              <select
                name="DataTables_Table_0_length"
                aria-controls="DataTables_Table_0"
                className="custom-select custom-select-sm form-control form-control-sm ml-2"
                style={{ width: "66px" }}
                onChange={handleSelectChange}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
              </select>
            </label>
          </div>
          <Dropdown>
            <Dropdown.Toggle
              variant=""
              id="dropdown-basic"
              title="Colonnes dʼaffichage"
            >
              <i className="las la-eye"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.id_notification}
                  onChange={() => handleColumnChange("id_notification")}
                />
                <span style={{ marginLeft: "10px" }}>
                  {translate("Notification ID")}
                </span>
              </Dropdown.Item>
              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.name_groupe_alarme}
                  onChange={() => handleColumnChange("name_groupe_alarme")}
                />
                <span style={{ marginLeft: "10px" }}>{translate("Group")}</span>
              </Dropdown.Item>
              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.name_alarme}
                  onChange={() => handleColumnChange("name_alarme")}
                />
                <span style={{ marginLeft: "10px" }}>
                  {translate("Alarms")}
                </span>
              </Dropdown.Item>
              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.message}
                  onChange={() => handleColumnChange("message")}
                />
                <span style={{ marginLeft: "10px" }}>
                  {translate("Details")}
                </span>
              </Dropdown.Item>
              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.attached}
                  onChange={() => handleColumnChange("attached")}
                />
                <span style={{ marginLeft: "10px" }}>
                  {translate("Attached to")}
                </span>
              </Dropdown.Item>

              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.date_start}
                  onChange={() => handleColumnChange("date_start")}
                />
                <span style={{ marginLeft: "10px" }}>
                  {translate("Start date")}
                </span>
              </Dropdown.Item>

              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.date_end}
                  onChange={() => handleColumnChange("date_end")}
                />
                <span style={{ marginLeft: "10px" }}>
                  {translate("End date")}
                </span>
              </Dropdown.Item>

              <Dropdown.Item
                as="button"
                style={{ display: "flex", alignItems: "center" }}
              ></Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      <div className="row m-1">
        <Table className="dataTable">
          <thead className="bg-white text-uppercase">
            <tr className="ligth ligth-data">
              <th>
                <div className="form-check form-check-inline">
                  <input className="form-check-input" type="checkbox" />
                  <label className="form-check-label"></label>
                </div>
              </th>
              {selectedColumns.id_notification && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("id_notification")}
                >
                  {translate("Code")}
                </th>
              )}
              {selectedColumns.name_groupe_alarme && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("name_groupe_alarme")}
                >
                  {translate("Group")}
                </th>
              )}
              {selectedColumns.name_alarme && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("name_alarme")}
                >
                  {translate("Alarmes")}
                </th>
              )}
              {selectedColumns.message && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("message")}
                >
                  {translate("Details")}
                </th>
              )}
              {selectedColumns.attached && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("attached")}
                >
                  {translate("Attached to")}
                </th>
              )}
              {selectedColumns.date_start && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("date_start")}
                >
                  {translate("Start date")}
                </th>
              )}

              {selectedColumns.date_end && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("date_end")}
                >
                  {translate("End date")}
                </th>
              )}
              {<th>{translate("Infos")}</th>}
            </tr>
          </thead>
          <tbody key="#" className="ligth-body">
            {loading ? (
              <tr>
                <td className="text-center" colSpan={7}>
                  <p>
                    <PropagateLoader
                      color={"#123abc"}
                      loading={loading}
                      size={20}
                    />
                  </p>
                </td>
              </tr>
            ) : list_Notifications.length > 0 ? (
              list_Notifications.map((notification) => (
                <tr key={notification.id_notification}>
                  <td>
                    <div className="form-check form-check-inline">
                      <input type="checkbox" className="form-check-input" />
                    </div>
                  </td>
                  {selectedColumns.id_notification && (
                    <td>{notification.id_notification}</td>
                  )}
                  {selectedColumns.name_groupe_alarme && (
                    <td>{notification.type === 'trip_detected' ? 'Mission' : notification.name_groupe_alarme}</td>
                  )}
                  {selectedColumns.name_alarme && (
                    <td>{notification.type === 'trip_detected' ? 'Trip détecté' : notification.name_alarme}</td>
                  )}
                  {selectedColumns.message && (
                    <td title={formatNotificationMessage(notification.message, translate)}>
                      {formatNotificationMessage(notification.message, translate)}
                      {notification.type === 'trip_detected' && (
                        <small className="d-block mt-1">
                          {notification.trip_event_status === 'arrived' ? 'Arrivée détectée le' : 'Retard détecté le'} {toTimestamp(notification.timestamp)}
                        </small>
                      )}
                      {notification.webhook_state && (
                        <div className="mt-2">
                          <span className={`badge badge-${notification.webhook_state === 'delivered' ? 'success' : notification.webhook_state === 'pending' ? 'secondary' : 'warning'}`}>
                            {{ pending: 'Webhook en attente', retry: 'Webhook en échec · réessai prévu', review: 'Webhook à vérifier', delivered: 'Webhook envoyé' }[notification.webhook_state]}
                          </span>
                          <small className="d-block mt-1">
                            {notification.webhook_attempts || 0} tentative(s)
                            {notification.webhook_http_status ? ` · HTTP ${notification.webhook_http_status}` : ''}
                            {notification.webhook_delivered_at ? ` · Envoyé le ${toTimestamp(notification.webhook_delivered_at)}` : ''}
                          </small>
                          {notification.webhook_error && <small className="d-block">{notification.webhook_error}</small>}
                        </div>
                      )}
                    </td>
                  )}
                  {selectedColumns.attached && (
                    <td>{notification.immatriculation_vehicule}</td>
                  )}
                  {selectedColumns.date_start && (
                    <td>{notification.type === 'trip_detected'
                      ? (notification.trip_created_at ? toTimestamp(notification.trip_created_at) : '—')
                      : toTimestamp(notification.timestamp)}</td>
                  )}
                  {selectedColumns.date_end && (
                    <td title={notification.type === 'trip_detected' && notification.trip_event_status !== 'arrived'
                      ? 'Aucune arrivée confirmée par cette notification' : undefined}>
                      {notification.type === 'trip_detected'
                        ? (notification.trip_event_status === 'arrived' ? toTimestamp(notification.timestamp) : '—')
                        : toTimestamp(notification.timestamp)}
                    </td>
                  )}
                  <td>
                    <div className="d-flex align-items-center list-action">
                      <Link
                        to={(() => {
                          const range = getPositionTrackingWindow(notification.timestamp);
                          return `/position-tracking/${range.from}/${range.to}/${notification.immatriculation_vehicule}`;
                        })()}
                        className="badge badge-success mr-2"
                        data-toggle="tooltip"
                        data-placement="top"
                        title={translate("Details")}
                      >
                        <i
                          className="las la-bell"
                          style={{ fontSize: "1.2em" }}
                        ></i>
                      </Link>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9}>No notification available</td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <div className="row">
        <div className="col-md-6 d-flex align-items-center">
          <span>
            {translate("Displaying")} {list_Notifications.length}{" "}
            {translate("out of")} {total}
          </span>
        </div>
        <div className="col-md-6">
          <ReactPaginate
            previousLabel={translate("previous")}
            nextLabel={translate("next")}
            breakLabel={"..."}
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            onPageChange={handlePageClick}
            containerClassName={"pagination justify-content-center"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            breakClassName={"page-item"}
            breakLinkClassName={"page-link"}
            activeClassName={"active"}
          />
        </div>
      </div>
    </>
  );
}
